import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";
import { upsertAwards } from "../api/award-api";
import { awardsQueryKey } from "./useAwards";
import { evaluationCompletionQueryKey } from "./useEvaluationCompletion";
import { isAwardDecisionFinal } from "../utils/awardErrors";
import type { AwardsResponse, UpsertAwardsRequest } from "../types/award.type";

/**
 * Mutation hook for upserting award decisions. On success it invalidates both
 * the shared `awardsQueryKey` and the `completion` key, so every consumer
 * (PanelMeetingStep cards, FinalReportAndAwardStep table + completion banner)
 * re-fetches the updated state.
 */
export const useUpsertAwards = (
  rfxId: number | undefined,
  evalId: number | undefined
): UseMutationResult<AwardsResponse, Error, UpsertAwardsRequest> => {
  const queryClient = useQueryClient();

  const invalidateAwardData = () => {
    queryClient.invalidateQueries({
      queryKey: awardsQueryKey(rfxId ?? 0, evalId ?? 0),
    });
    queryClient.invalidateQueries({
      queryKey: evaluationCompletionQueryKey(rfxId ?? 0, evalId ?? 0),
    });
  };

  return useMutation({
    mutationFn: (payload: UpsertAwardsRequest) =>
      upsertAwards(rfxId!, evalId!, payload),
    onSuccess: invalidateAwardData,
    onError: (error) => {
      // A 409 AWARD_DECISION_FINAL means another user already finalised the
      // decision — our cache is stale, so refetch awards + completion even
      // though the mutation rejected (onSuccess does not run on rejection).
      if (isAwardDecisionFinal(error)) {
        invalidateAwardData();
      }
    },
  });
};
